(function () {
  const PRODUCT_INFO_SELECTOR = 'product-info[id^="MainProduct"]';

  // Gates "Add to cart" behind an explicit choice of the non-swatch options
  // (e.g. Size). Swatch options (e.g. Colour) keep their on-page default.
  class SizeSelectionGate {
    constructor(productInfo) {
      // Quick-add reuses the main product markup; it keeps its own flow.
      if (productInfo.closest('quick-add-modal')) return;

      this.productInfo = productInfo;
      this.sectionId = productInfo.dataset.section;
      this.form = productInfo.querySelector('form[data-type="add-to-cart-form"]');
      this.modal = document.getElementById(`SizePickerModal-${this.sectionId}`);
      if (!this.form || !this.modal) return;

      this.submitButton = this.form.querySelector('[name="add"]');
      this.gatedPositions = this.getGatedPositions();
      if (!this.gatedPositions.length) return;

      this.pickedPositions = new Set();
      this.pendingAdd = false;
      this.availability = this.parseAvailability();

      // Gated options (e.g. Size) are never preselected — not even from a ?variant=
      // URL. Only an explicit user interaction chooses one.
      this.deselectUnpickedGated();
      this.ensureAddButtonState();

      // Capture-phase so this runs before the theme's (or GoKwik's) own add-to-cart
      // handler, whether that fires on click or on form submit.
      this.modalSubmit = this.modal.querySelector('.size-picker-modal__submit');

      this.productInfo.addEventListener('click', this.onAddClickCapture.bind(this), true);
      this.productInfo.addEventListener('submit', this.onSubmitCapture.bind(this), true);
      this.productInfo.addEventListener('change', this.onPageChange.bind(this), true);
      this.modal.addEventListener('change', this.onModalChange.bind(this));
      if (this.modalSubmit) this.modalSubmit.addEventListener('click', this.onModalSubmit.bind(this));
      subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
    }

    get variantSelects() {
      return this.productInfo.querySelector('variant-selects');
    }

    gatedFieldsets() {
      const vs = this.variantSelects;
      return vs ? Array.from(vs.querySelectorAll('[data-gated-option]')) : [];
    }

    getGatedPositions() {
      return this.gatedFieldsets().map((el) => el.getAttribute('data-gated-option'));
    }

    parseAvailability() {
      const el = this.modal.querySelector('[data-variant-availability]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    // Values chosen for the non-gated options (e.g. the selected Colour).
    selectedContextValues() {
      const vs = this.variantSelects;
      if (!vs) return [];
      return Array.from(vs.querySelectorAll('input:checked'))
        .filter((input) => !input.closest('[data-gated-option]'))
        .map((input) => input.value);
    }

    // Is any variant available for the currently-selected context (e.g. colour)?
    hasAvailableForContext() {
      if (!this.availability) return true;
      const context = this.selectedContextValues();
      return this.availability.some(
        (variant) => variant.a && context.every((value) => variant.o.indexOf(value) !== -1)
      );
    }

    // With no size chosen yet, the add-to-cart button's job is to open the size
    // popup — so keep it enabled whenever a size is still available (even if the
    // SSR/URL variant is a sold-out combination).
    ensureAddButtonState() {
      if (!this.submitButton || this.allGatedPicked()) return;
      if (!this.hasAvailableForContext()) return; // genuinely nothing available here
      this.submitButton.removeAttribute('disabled');
      this.submitButton.removeAttribute('aria-disabled');
      const label = this.submitButton.querySelector('span');
      if (label && window.variantStrings && window.variantStrings.addToCart) {
        label.textContent = window.variantStrings.addToCart;
      }
    }

    // Grey out sizes that aren't available in the currently-selected colour so a
    // shopper can't pick an out-of-stock combination.
    updatePopupAvailability() {
      if (!this.availability) return;
      const context = this.selectedContextValues();
      this.modal.querySelectorAll('.size-picker-modal__input').forEach((input) => {
        const value = input.dataset.value;
        if (value == null) return;
        const available = this.availability.some(
          (variant) =>
            variant.a &&
            variant.o.indexOf(value) !== -1 &&
            context.every((contextValue) => variant.o.indexOf(contextValue) !== -1)
        );
        input.disabled = !available;
        input.classList.toggle('disabled', !available);
      });
    }

    // Uncheck every gated option the shopper hasn't explicitly chosen. Dawn marks
    // a default on the first render and on every re-render (e.g. colour change),
    // so this runs again after each variant change.
    deselectUnpickedGated() {
      this.gatedFieldsets().forEach((fieldset) => {
        if (this.pickedPositions.has(fieldset.getAttribute('data-gated-option'))) return;
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = false;
          input.removeAttribute('checked');
        });
      });
    }

    allGatedPicked() {
      return this.gatedPositions.every((position) => this.pickedPositions.has(position));
    }

    onPageChange(event) {
      const fieldset = event.target.closest && event.target.closest('[data-gated-option]');
      if (!fieldset) return;
      if (event.target.matches('input[type="radio"]') && event.target.checked) {
        this.pickedPositions.add(fieldset.getAttribute('data-gated-option'));
      }
    }

    onVariantChange(event) {
      if (!event || !event.data || event.data.sectionId !== this.sectionId) return;

      this.deselectUnpickedGated();
      this.ensureAddButtonState();

      if (this.pendingAdd && this.allGatedPicked()) {
        this.pendingAdd = false;
        clearTimeout(this.fallbackTimer);
        const variant = event.data.variant;
        if (variant && variant.available) {
          this.submitAddToCart();
        } else {
          // Chosen size isn't available in the selected colour — reset the size
          // choice and keep the popup open with the selectable sizes refreshed.
          this.pickedPositions.clear();
          this.deselectUnpickedGated();
          this.resetModalState();
          this.updatePopupAvailability();
        }
      }
    }

    onAddClickCapture(event) {
      const button = event.target.closest && event.target.closest('.product-form__submit');
      if (!button || !this.form.contains(button)) return;
      if (this.adding || this.allGatedPicked()) return; // size chosen — add to cart normally

      event.preventDefault();
      event.stopImmediatePropagation();
      this.openModal();
    }

    onSubmitCapture(event) {
      if (event.target !== this.form) return;
      if (this.adding || this.allGatedPicked()) return; // size chosen — add to cart normally

      event.preventDefault();
      event.stopImmediatePropagation();
      this.openModal();
    }

    openModal() {
      this.resetModalState();
      this.updatePopupAvailability();
      if (typeof this.modal.show === 'function') {
        this.modal.show(this.submitButton);
      } else {
        document.body.classList.add('overflow-hidden');
        this.modal.setAttribute('open', '');
      }
    }

    resetModalState() {
      this.modal.classList.remove('size-picker-modal--adding');
      this.modal.querySelectorAll('.size-picker-modal__input').forEach((input) => {
        input.checked = false;
      });
      if (this.modalSubmit) this.modalSubmit.disabled = true;
    }

    onModalChange(event) {
      const input = event.target.closest('.size-picker-modal__input');
      if (!input || input.disabled) return;
      // Selecting a size only enables the Add-to-cart button — it doesn't add yet.
      this.updateModalSubmitState();
    }

    updateModalSubmitState() {
      if (!this.modalSubmit) return;
      const allChosen = this.gatedPositions.every((position) =>
        this.modal.querySelector(`.size-picker-modal__input[data-option-position="${position}"]:checked`)
      );
      this.modalSubmit.disabled = !allChosen;
    }

    onModalSubmit() {
      if (this.pendingAdd) return;
      const chosen = [];
      for (let i = 0; i < this.gatedPositions.length; i++) {
        const position = this.gatedPositions[i];
        const checked = this.modal.querySelector(
          `.size-picker-modal__input[data-option-position="${position}"]:checked`
        );
        if (!checked) return; // button is disabled until every option is chosen
        chosen.push({ position, valueId: checked.dataset.optionValueId });
      }
      this.addChosenToCart(chosen);
    }

    addChosenToCart(chosen) {
      const vs = this.variantSelects;
      if (!vs) return;

      // Apply each chosen value to the on-page picker. Mark as picked BEFORE
      // dispatching so onVariantChange keeps the selection.
      let lastRadio = null;
      chosen.forEach(({ position, valueId }) => {
        const pageRadio = vs.querySelector(
          `[data-gated-option="${position}"] input[type="radio"][data-option-value-id="${valueId}"]`
        );
        if (!pageRadio) return;
        this.pickedPositions.add(position);
        pageRadio.checked = true;
        lastRadio = pageRadio;
      });

      if (!lastRadio) return;

      this.pendingAdd = true;
      this.modal.classList.add('size-picker-modal--adding');
      if (this.modalSubmit) this.modalSubmit.disabled = true;
      // Safety net: release if the variant never resolves.
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = setTimeout(() => {
        this.pendingAdd = false;
        this.modal.classList.remove('size-picker-modal--adding');
      }, 8000);

      // A single change dispatch resolves the full variant (colour + chosen sizes).
      lastRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    submitAddToCart() {
      if (this.submitButton) this.submitButton.removeAttribute('disabled');

      if (typeof this.modal.hide === 'function') {
        this.modal.hide();
      } else {
        this.modal.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
      }
      this.modal.classList.remove('size-picker-modal--adding');

      // Trigger the theme's real add-to-cart. A click works whether the theme
      // adds via form submit (Dawn) or via a click handler (e.g. GoKwik).
      this.adding = true;
      if (this.submitButton) this.submitButton.click();
      else if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
      setTimeout(() => {
        this.adding = false;
      }, 0);
    }
  }

  function init(root) {
    (root || document).querySelectorAll(PRODUCT_INFO_SELECTOR).forEach((productInfo) => {
      if (productInfo.sizeSelectionGate) return;
      productInfo.sizeSelectionGate = new SizeSelectionGate(productInfo);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
